import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS } from "../theme";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";

function horaLocal(iso) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}
function dataHoraLocal(iso) {
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function formatarDuracao(segundos) {
  if (segundos == null) return "—";
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min`;
  return `${segundos}s`;
}
function segundosEntre(a, b) {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000);
}

export default function HistoricoRotas() {
  const { token } = useAuth();
  const [execucoes, setExecucoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionada, setSelecionada] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const [filtroRota, setFiltroRota] = useState("");
  const [filtroFuncionario, setFiltroFuncionario] = useState("");
  const [filtroInicio, setFiltroInicio] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/rotas-execucoes", undefined, token);
      setExecucoes(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const selecionar = async (execucao) => {
    setSelecionada(execucao);
    setCarregandoDetalhe(true);
    try {
      const data = await apiFetch("GET", `/api/rotas-execucoes/${execucao.id}/detalhe`, undefined, token);
      setDetalhe(data);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  if (loading) return <EstadoCarregando />;

  const rotasDisponiveis = [...new Set(execucoes.map((e) => e.rota_nome).filter(Boolean))].sort();
  const execucoesFiltradas = execucoes.filter((e) => {
    if (filtroRota && e.rota_nome !== filtroRota) return false;
    if (filtroFuncionario && !e.funcionario_nome.toLowerCase().includes(filtroFuncionario.toLowerCase())) return false;
    if (filtroInicio) {
      const d = new Date(e.iniciado_em);
      const localStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (localStr !== filtroInicio) return false;
    }
    if (filtroStatus && e.status !== filtroStatus) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroRota || filtroFuncionario || filtroInicio || filtroStatus);
  const limparFiltros = () => { setFiltroRota(""); setFiltroFuncionario(""); setFiltroInicio(""); setFiltroStatus(""); };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Histórico de Rotas
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Execuções de rondas/limpeza — horário de cada ponto e duração.
        </p>
      </div>

      {execucoes.length > 0 && (
        <BarraResumoFiltro
          total={execucoesFiltradas.length}
          totalGeral={execucoes.length}
          unidade="execução(ões)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <div className="split-layout">
        <Card style={{ padding: 0, flex: 1 }}>
          {execucoes.length === 0 ? (
            <EstadoVazio icon="route" texto="Nenhuma execução de rota registrada ainda." />
          ) : execucoesFiltradas.length === 0 ? (
            <EstadoVazio icon="route" texto="Nenhuma execução encontrada com esse filtro." />
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <ThFiltroSelect
                    label="Rota" value={filtroRota} onChange={setFiltroRota} minWidth={150}
                    opcoes={[{ value: "", label: "Todas" }, ...rotasDisponiveis.map((r) => ({ value: r, label: r }))]}
                  />
                  <ThFiltroTexto label="Funcionário" value={filtroFuncionario} onChange={setFiltroFuncionario} placeholder="Buscar nome..." minWidth={150} />
                  <ThFiltroTexto label="Início" type="date" value={filtroInicio} onChange={setFiltroInicio} minWidth={140} />
                  <ThSimples label="Progresso" />
                  <ThSimples label="Duração" />
                  <ThFiltroSelect
                    label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={140}
                    opcoes={[{ value: "", label: "Todos" }, { value: "concluida", label: "Concluída" }, { value: "em_andamento", label: "Em andamento" }]}
                  />
                </tr>
              </thead>
              <tbody>
                {execucoesFiltradas.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => selecionar(e)}
                    style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: selecionada?.id === e.id ? COLORS.primaryBg : "transparent" }}
                  >
                    <td style={{ padding: "14px 20px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{e.rota_nome}</td>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{e.funcionario_nome}</td>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{dataHoraLocal(e.iniciado_em)}</td>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{e.total_validados}/{e.total_pontos}</td>
                    <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>
                      {e.finalizado_em ? formatarDuracao(segundosEntre(e.iniciado_em, e.finalizado_em)) : "—"}
                    </td>
                    <td style={{ padding: "14px 20px" }}>
                      <Badge tone={e.status === "concluida" ? "success" : "primary"}>
                        {e.status === "concluida" ? "Concluída" : "Em andamento"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card style={{ width: 340, flexShrink: 0 }} className="split-layout-sidebar">
          {!selecionada ? (
            <div style={{ fontSize: 13.5, color: COLORS.textMid }}>Clique em uma execução pra ver o detalhe de cada ponto.</div>
          ) : carregandoDetalhe ? (
            <EstadoCarregando padding={0} />
          ) : (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 2px" }}>{detalhe.rota_nome}</h3>
              <p style={{ fontSize: 12.5, color: COLORS.textMid, margin: "0 0 16px" }}>{detalhe.funcionario_nome}</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {detalhe.pontos.map((p) => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      background: p.validado ? COLORS.successBg : COLORS.bg, color: p.validado ? COLORS.success : COLORS.textLight,
                    }}>
                      {p.validado ? <Icon name="checkCircle" size={13} /> : <span style={{ fontSize: 11, fontWeight: 700 }}>{p.ordem}</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{p.nome}</div>
                      {!p.validado && <div style={{ fontSize: 11, color: COLORS.textLight }}>não validado</div>}
                    </div>
                    {p.validado && (
                      <div style={{ textAlign: "right", fontSize: 11.5, color: COLORS.textMid }}>
                        <div>{horaLocal(p.validado_em)}</div>
                        {p.segundos_desde_anterior != null && (
                          <div style={{ color: COLORS.textLight }}>+{formatarDuracao(p.segundos_desde_anterior)}</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 16, paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: COLORS.textMid }}>Duração total</span>
                  <span style={{ fontWeight: 700, color: COLORS.text }}>
                    {detalhe.finalizado_em ? formatarDuracao(segundosEntre(detalhe.iniciado_em, detalhe.finalizado_em)) : "em andamento"}
                  </span>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
